<template>
    <div
        v-if="currentItem"
        :class="[$style.wrapper, isAnimating && $style.fade_leave]"
        @click="handleClick"
    >
        <Typography
            v-if="currentItem.type === 'focus'"
            size="s"
            type="paragraph"
        >
            {{ $tr('gameContainer.focus_overlay_text') }}
        </Typography>
        <Typography
            v-else-if="currentItem.type === 'swipe'"
            size="s"
            type="paragraph"
        >
            {{ $tr('gameContainer.mobile_safari_swipe_to_hide_hint') }}
        </Typography>
        <template v-else-if="currentItem.type === 'rotate'">
            <img
                :src="RotateIcon"
                alt="Rotate icon"
                :class="screenOrientation === 'landscape' && $style.rotated_icon"
            />
            <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component-->
            <Typography
                size="s"
                type="paragraph"
                :class="$style.label"
                v-html="
                    $tr('gameRotateBanner.message', {
                        orientation:
                            currentItem.rotateOrientation === 'landscape'
                                ? $tr('orientationLabel.horizontalOrientation')
                                : $tr('orientationLabel.verticalOrientation'),
                    })
                "
            />
            <!-- eslint-enable vue/no-v-html vue/no-v-text-v-html-on-component -->
        </template>
    </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'

import Typography from '@/components/Typography.vue'
import { useLayoutStore } from '@/store/layout-store'

import RotateIcon from './images/rotate.svg'

export type NotifierMode = 'focus' | 'rotate' | 'swipe'
export type Orientation = 'landscape' | 'portrait'

export interface NotifierItem {
    type: NotifierMode
    rotateOrientation?: Orientation
}

const TIMEOUT_DURATION = 15_000 // 15 seconds
const ANIMATION_DURATION = 300 // 300ms

const props = defineProps<{
    queue: NotifierItem[]
}>()

const emit = defineEmits<{
    click: []
}>()

const layoutStore = useLayoutStore()
const { screenOrientation } = storeToRefs(layoutStore)

const dismissedTypes = shallowRef(new Set<NotifierMode>())
const isAnimating = ref(false)
const autoCloseTimer = ref<number>()

// Find first non-dismissed item in queue
const currentItem = computed(() => props.queue.find((item) => !dismissedTypes.value.has(item.type)))

function closeWithAnimation() {
    isAnimating.value = true
    setTimeout(() => {
        if (currentItem.value) {
            dismissedTypes.value = new Set([...dismissedTypes.value, currentItem.value.type])
        }
        isAnimating.value = false
    }, ANIMATION_DURATION)
}

function handleClick() {
    emit('click')
    closeWithAnimation()
}

function startAutoCloseTimer() {
    clearAutoCloseTimer()
    autoCloseTimer.value = window.setTimeout(() => {
        closeWithAnimation()
    }, TIMEOUT_DURATION)
}

function clearAutoCloseTimer() {
    if (autoCloseTimer.value) {
        window.clearTimeout(autoCloseTimer.value)
        autoCloseTimer.value = undefined
    }
}

// When queue changes, clean up dismissed types that are no longer in queue
// This allows items to reappear when they become active again
watch(
    () => props.queue,
    (queue) => {
        const newDismissed = new Set<NotifierMode>()
        for (const type of dismissedTypes.value) {
            if (queue.find((i) => i.type === type)) {
                newDismissed.add(type)
            }
        }
        if (newDismissed.size !== dismissedTypes.value.size) {
            dismissedTypes.value = newDismissed
        }
    },
)

// Restart timer when current item changes
watch(currentItem, (newItem) => {
    if (newItem) {
        startAutoCloseTimer()
    } else {
        clearAutoCloseTimer()
    }
})

onMounted(() => {
    if (currentItem.value) {
        startAutoCloseTimer()
    }
})

onBeforeUnmount(() => {
    clearAutoCloseTimer()
})
</script>

<style module>
.wrapper {
    display: flex;
    padding: 12px 16px 12px 12px;
    align-items: center;
    gap: 12px;
    border-radius: 16px;
    background: rgba(29, 28, 38, 0.64);
    backdrop-filter: blur(8px);
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translate(-50%, 0);
    cursor: pointer;
}

.fade_leave {
    transform: translate(-50%, -20px);
    opacity: 0;
    transition:
        transform 0.3s ease-out,
        opacity 0.3s ease-out;
}

.rotated_icon {
    transform: rotate(-90deg);
}

.label {
    text-transform: lowercase;
}

.label::first-letter {
    text-transform: uppercase;
}
</style>
